import axios from "../config/axios";
import {
  AdminConsoleMeta,
  ManagedAdmin,
  ManagedDomain,
  ManagedRole,
  ManagedUser,
  PaginationResult,
  TrashResponse,
} from "../types";

class AdminConsoleService {
  async getMeta(): Promise<AdminConsoleMeta> {
    const response = await axios.get<AdminConsoleMeta>("/api/admin-console/meta");
    return response.data;
  }

  async getUsers(params: Record<string, unknown>): Promise<PaginationResult<ManagedUser>> {
    const response = await axios.get<PaginationResult<ManagedUser>>("/api/admin-console/users", { params });
    return response.data;
  }

  async getUser(id: string): Promise<ManagedUser> {
    const response = await axios.get<ManagedUser>(`/api/admin-console/users/${id}`);
    return response.data;
  }

  async createUser(payload: Record<string, unknown>): Promise<ManagedUser> {
    const response = await axios.post<ManagedUser>("/api/admin-console/users", payload);
    return response.data;
  }

  async updateUser(id: string, payload: Record<string, unknown>): Promise<ManagedUser> {
    const response = await axios.patch<ManagedUser>(`/api/admin-console/users/${id}`, payload);
    return response.data;
  }

  async deleteUser(id: string): Promise<void> {
    await axios.delete(`/api/admin-console/users/${id}`);
  }

  async restoreUser(id: string): Promise<ManagedUser> {
    const response = await axios.post<ManagedUser>(`/api/admin-console/users/${id}/restore`);
    return response.data;
  }

  async permanentlyDeleteUser(id: string): Promise<void> {
    await axios.delete(`/api/admin-console/users/${id}/permanent`);
  }

  async getAdmins(params: Record<string, unknown>): Promise<PaginationResult<ManagedAdmin>> {
    const response = await axios.get<PaginationResult<ManagedAdmin>>("/api/admin-console/admins", { params });
    return response.data;
  }

  async getAdmin(id: string): Promise<ManagedAdmin> {
    const response = await axios.get<ManagedAdmin>(`/api/admin-console/admins/${id}`);
    return response.data;
  }

  async createAdmin(payload: Record<string, unknown>): Promise<ManagedAdmin> {
    const response = await axios.post<ManagedAdmin>("/api/admin-console/admins", payload);
    return response.data;
  }

  async updateAdmin(id: string, payload: Record<string, unknown>): Promise<ManagedAdmin> {
    const response = await axios.patch<ManagedAdmin>(`/api/admin-console/admins/${id}`, payload);
    return response.data;
  }

  async deleteAdmin(id: string): Promise<void> {
    await axios.delete(`/api/admin-console/admins/${id}`);
  }

  async restoreAdmin(id: string): Promise<ManagedAdmin> {
    const response = await axios.post<ManagedAdmin>(`/api/admin-console/admins/${id}/restore`);
    return response.data;
  }

  async permanentlyDeleteAdmin(id: string): Promise<void> {
    await axios.delete(`/api/admin-console/admins/${id}/permanent`);
  }

  async getDomains(params: Record<string, unknown>): Promise<PaginationResult<ManagedDomain>> {
    const response = await axios.get<PaginationResult<ManagedDomain>>("/api/admin-console/domains", { params });
    return response.data;
  }

  async getDomain(id: string): Promise<ManagedDomain> {
    const response = await axios.get<ManagedDomain>(`/api/admin-console/domains/${id}`);
    return response.data;
  }

  async createDomain(payload: Record<string, unknown>): Promise<ManagedDomain> {
    const response = await axios.post<ManagedDomain>("/api/admin-console/domains", payload);
    return response.data;
  }

  async updateDomain(id: string, payload: Record<string, unknown>): Promise<ManagedDomain> {
    const response = await axios.patch<ManagedDomain>(`/api/admin-console/domains/${id}`, payload);
    return response.data;
  }

  async deleteDomain(id: string): Promise<void> {
    await axios.delete(`/api/admin-console/domains/${id}`);
  }

  async restoreDomain(id: string): Promise<ManagedDomain> {
    const response = await axios.post<ManagedDomain>(`/api/admin-console/domains/${id}/restore`);
    return response.data;
  }

  async permanentlyDeleteDomain(id: string): Promise<void> {
    await axios.delete(`/api/admin-console/domains/${id}/permanent`);
  }

  async getRoles(params: Record<string, unknown>): Promise<PaginationResult<ManagedRole>> {
    const response = await axios.get<PaginationResult<ManagedRole>>("/api/admin-console/roles", { params });
    return response.data;
  }

  async getRole(id: string): Promise<ManagedRole> {
    const response = await axios.get<ManagedRole>(`/api/admin-console/roles/${id}`);
    return response.data;
  }

  async createRole(payload: Record<string, unknown>): Promise<ManagedRole> {
    const response = await axios.post<ManagedRole>("/api/admin-console/roles", payload);
    return response.data;
  }

  async updateRole(id: string, payload: Record<string, unknown>): Promise<ManagedRole> {
    const response = await axios.patch<ManagedRole>(`/api/admin-console/roles/${id}`, payload);
    return response.data;
  }

  async deleteRole(id: string): Promise<void> {
    await axios.delete(`/api/admin-console/roles/${id}`);
  }

  async restoreRole(id: string): Promise<ManagedRole> {
    const response = await axios.post<ManagedRole>(`/api/admin-console/roles/${id}/restore`);
    return response.data;
  }

  async permanentlyDeleteRole(id: string): Promise<void> {
    await axios.delete(`/api/admin-console/roles/${id}/permanent`);
  }

  async getTrash(params: Record<string, unknown>): Promise<TrashResponse> {
    const response = await axios.get<TrashResponse>("/api/admin-console/trash", { params });
    return response.data;
  }
}

const adminConsoleService = new AdminConsoleService();

export default adminConsoleService;
